import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useInterval } from 'react-use';

import { datadogRum } from '@datadog/browser-rum';

import { useSageAuth } from 'src/auth/useSageAuth';
import { AUTH0_CALLBACK_PATH, SSO_DIRECT_LOGIN_PATH } from 'src/constants/Auth0';
import useLogout from 'src/hooks/useLogout';
import { LogoutDialogTypes } from 'src/pages/LoginPage';
import {
    clearTokenRefreshRequest,
    selectBearerToken,
    selectIsRefreshNeeded,
    setBearerToken,
    setHasCheckedToken,
} from 'src/store/authSlice';
import { resolveTokenError, TokenErrorResolution } from 'src/utils/authCallbackUtils';

// 30s. getAccessToken serves most ticks from cache and hits the network only at real expiry.
const REFRESH_INTERVAL = 1000 * 30;

const AuthTokenCheck = ({ isProcessingLogin }: { isProcessingLogin: boolean }) => {
    const { isLoading, isAuthenticated, getAccessToken } = useSageAuth();
    const { logout, logoutWithoutAuth0Redirect } = useLogout();
    const dispatch = useDispatch();
    const location = useLocation();

    const bearerToken = useSelector(selectBearerToken);
    const isRefreshNeeded = useSelector(selectIsRefreshNeeded);

    const isCheckingRef = useRef(false);
    // Read inside checkToken without making it a dependency, which would re-run the mount check
    const hasSessionRef = useRef(false);

    useEffect(() => {
        hasSessionRef.current = isAuthenticated || !!bearerToken;
    }, [bearerToken, isAuthenticated]);

    const checkToken = useCallback(async () => {
        if (isCheckingRef.current) return;
        isCheckingRef.current = true;

        const attemptGetToken = async (isRetry: boolean) => {
            try {
                // Success means the session is healthy and the token is current
                const token = await getAccessToken();

                dispatch(setBearerToken(`Bearer ${token}`));
            } catch (error: any) {
                const resolution = resolveTokenError({
                    hasSession: hasSessionRef.current,
                    isRetry,
                    error,
                });

                if (resolution === TokenErrorResolution.LoginRequired) {
                    await logoutWithoutAuth0Redirect(LogoutDialogTypes.LoginRequired);
                } else if (resolution === TokenErrorResolution.VerificationRequired) {
                    datadogRum.addError(error, {
                        message: 'Silent token request blocked by adaptive MFA',
                    });
                    await logoutWithoutAuth0Redirect(LogoutDialogTypes.VerificationRequired);
                } else if (resolution === TokenErrorResolution.Retry) {
                    await attemptGetToken(true);
                } else if (resolution === TokenErrorResolution.Logout) {
                    datadogRum.addError(error, {
                        message: 'Error getting token on retry',
                    });
                    logout(LogoutDialogTypes.TokenError);
                }
                // Ignore: there is no session to end — see resolveTokenError
            }
        };

        try {
            await attemptGetToken(false);
        } finally {
            isCheckingRef.current = false;
            dispatch(setHasCheckedToken());
        }
    }, [dispatch, getAccessToken, logout, logoutWithoutAuth0Redirect]);

    // Triggered reactively whenever a base query receives a 401 and dispatches
    // requestTokenRefresh. Calls the existing checkToken flow, which
    // refreshes the token or logs the user out — all within React.
    useEffect(() => {
        if (!isRefreshNeeded) return;
        checkToken().finally(() => dispatch(clearTokenRefreshRequest()));
    }, [checkToken, dispatch, isRefreshNeeded]);

    // Check token on mount, only if not already processing a login. Until the facade has picked an
    // owner it answers for the web SDK, which reports a shell-owned session as login_required.
    useEffect(() => {
        if (isProcessingLogin || isLoading) return;

        checkToken();
    }, [checkToken, isLoading, isProcessingLogin]);

    // Check token claims at a regular interval when authenticated. Safe for a shell-owned session:
    // the facade routes this to the shell, which stays the only refresh-token holder.
    useInterval(
        () => checkToken(),
        isAuthenticated &&
            !isLoading &&
            location?.pathname !== AUTH0_CALLBACK_PATH &&
            location?.pathname !== SSO_DIRECT_LOGIN_PATH
            ? REFRESH_INTERVAL
            : null
    );

    return <div />;
};

export default AuthTokenCheck;
